import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API_BASE = `${BACKEND_URL}/api`;

const api = axios.create({
  baseURL: API_BASE,
  headers: {
    'Content-Type': 'application/json',
  },
});

api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

api.interceptors.response.use(
  (response) => response,
  (error) => {
    // A 401 normally means the session is invalid, so we clear it and send the user to login.
    // Read-only, observer-only calls (e.g. the Experience FlowBar's recommendations fetch)
    // opt out with { skipAuthRedirect: true } so a background read can never eject the session.
    if (error.response?.status === 401 && !error.config?.skipAuthRedirect) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// Auth APIs
export const authAPI = {
  register: (data) => api.post('/auth/register', data),
  login: (data) => api.post('/auth/login', data),
  getMe: () => api.get('/auth/me'),
  logout: () => api.post('/auth/logout'),
  verifyEmail: (token) => api.get(`/auth/verify-email/${encodeURIComponent(token)}`),
  resendVerification: () => api.post('/auth/resend-verification'),
};

// User account / GDPR data-subject APIs
export const userAPI = {
  // GDPR Art. 20 — right to data portability. Returns the caller's data as JSON.
  exportData: () => api.get('/users/me/export'),
  // GDPR Art. 17 — right to erasure. Password-gated anonymize + deactivate.
  deleteAccount: (password) => api.post('/users/me/delete', { password }),
};

// Location APIs
export const locationAPI = {
  getAll: (params) => api.get('/locations', { params }),
  getById: (id) => api.get(`/locations/${id}`),
  create: (data) => api.post('/locations', data),
};

// Military base registry (area-level distance)
export const basesAPI = {
  getAll: (params) => api.get('/bases', { params }),
};

// Listing APIs
export const listingAPI = {
  getAll: (params) => api.get('/listings', { params }),
  getById: (id) => api.get(`/listings/${id}`),
  create: (data) => api.post('/listings', data),
  update: (id, data) => api.patch(`/listings/${id}`, data),
  addPhoto: (id, params) => api.post(`/listings/${id}/photos`, null, { params }),
  uploadPhotos: (id, formData, onProgress) =>
    api.post(`/listings/${id}/photos/upload`, formData, {
      headers: { 'Content-Type': undefined }, // clear instance default; axios auto-sets multipart/form-data + boundary
      onUploadProgress: (e) => {
        if (onProgress && e.total) onProgress(Math.round((e.loaded * 100) / e.total));
      },
    }),
  publish: (id) => api.post(`/listings/${id}/publish`),
  setStatus: (id, status) => api.patch(`/listings/${id}`, { status }),
  deleteListing: (id) => api.delete(`/listings/${id}`),
};

export const listingReviewAPI = {
  sendBatch: (listingId, data) => api.post(`/listing-review-batches/admin/listings/${listingId}/send`, data),
  getForListing: (listingId) => api.get(`/listing-review-batches/listings/${listingId}`),
  markAddressed: (annotationId) => api.patch(`/listing-review-batches/annotations/${annotationId}/addressed`),
};

// Billing APIs
export const billingAPI = {
  getPlans: () => api.get('/billing/plans'),
  getMyPlan: () => api.get('/billing/my-plan'),
  setPlan: (data) => api.post('/billing/set-plan', data),
  getHistory: () => api.get('/billing/history'),
};

// Owner APIs
export const ownerAPI = {
  getListings: () => api.get('/owner/listings'),
  getStats: () => api.get('/owner/stats'),
  getAnalytics: () => api.get('/owner/analytics'),
};

// Review APIs
export const reviewAPI = {
  getForListing: (listingId) => api.get('/reviews', { params: { listing_id: listingId, status: 'APPROVED' } }),
  create: (data) => api.post('/reviews', data),
  getPending: () => api.get('/reviews/pending'),
  moderate: (id, data) => api.put(`/reviews/${id}/moderate`, data),
};

// Alert APIs (Notify Me)
export const alertAPI = {
  getAll: () => api.get('/alerts'),
  create: (data) => api.post('/alerts', data),
  // Backend PATCH /alerts/{id} reads is_active from query params.
  update: (id, params) => api.patch(`/alerts/${id}`, null, { params }),
  delete: (id) => api.delete(`/alerts/${id}`),
};

// Notification APIs
export const notificationAPI = {
  getAll: (params) => api.get('/notifications', { params }),
  markRead: (id) => api.put(`/notifications/${id}/read`),
  markAllRead: () => api.put('/notifications/read-all'),
  delete: (id) => api.delete(`/notifications/${id}`),
};

// Walkthrough APIs
export const walkthroughAPI = {
  getAll: () => api.get('/walkthroughs'),
  create: (data) => api.post('/walkthroughs', data),
};

// AI APIs
export const aiAPI = {
  locationMatch: (data) => api.post('/ai/location-match', data),
  concierge: (data) => api.post('/ai/concierge', data),
  hiddenGems: (data) => api.post('/ai/hidden-gems', data),
  reviewAssist: (params) => api.post('/ai/review-assist', null, { params }),
};

// Admin APIs
export const adminAPI = {
  getStats: () => api.get('/admin/stats'),
  getUsers: (params) => api.get('/admin/users', { params }),
  updateUserRole: (userId, role) => api.put(`/admin/users/${userId}/role`, null, { params: { role } }),
  updateUser: (userId, params) => api.patch(`/admin/users/${userId}`, null, { params }),
  updateUserPlan: (userId, tier) => api.patch(`/admin/users/${userId}/plan`, { tier }),
  getPendingReviews: () => api.get('/reviews/pending'),
  getPendingListings: () => api.get('/admin/pending-listings'),
  rejectListing: (listingId, reason) => api.post(`/admin/listings/${listingId}/reject`, { reason }),
  assignReviewer: (listingId, reviewerId) => api.post(`/admin/listings/${listingId}/assign`, { reviewer_id: reviewerId ?? null }),
  getOpsSummary: () => api.get('/admin/ops-summary'),
  postSentryTest: () => api.post('/admin/ops/sentry-test'),
  getContactInquiries: (params) => api.get('/contact/inquiries', { params }),
};

// Life Journey Engine APIs (opt-in relocation journeys)
export const journeyAPI = {
  getTemplates: () => api.get('/journeys/templates'),
  getMine: () => api.get('/journeys/me'),
  start: (data) => api.post('/journeys/me/start', data),
  updateGoals: (data) => api.put('/journeys/me/goals', data),
  updateStatus: (status) => api.patch('/journeys/me/status', { status }),
  applyTaskAction: (taskId, data) => api.patch(`/journeys/me/tasks/${taskId}`, data),
  getDueReminders: (deliver = false) => api.get('/journeys/me/reminders/due', { params: { deliver } }),
  getPreferences: () => api.get('/journeys/me/preferences'),
  updatePreferences: (data) => api.put('/journeys/me/preferences', data),
  createHelpRequest: (data) => api.post('/journeys/me/help-requests', data),
  getHelpRequests: () => api.get('/journeys/me/help-requests'),
  cancelHelpRequest: (id) => api.patch(`/journeys/me/help-requests/${id}`, { status: 'cancelled' }),
  // Observer-only read used by the Experience FlowBar; must never trigger the global
  // 401 auto-logout (see the response interceptor's skipAuthRedirect guard).
  getRecommendations: () => api.get('/journeys/me/recommendations', { skipAuthRedirect: true }),
  getAudit: () => api.get('/journeys/me/audit'),
  deleteMyData: () => api.delete('/journeys/me'),
};

// Marketing Studio APIs (admin-only, gated by MARKETING_STUDIO_ENABLED on the backend)
export const marketingStudioAPI = {
  listWorkspaces: (params) => api.get('/admin/marketing/workspaces', { params }),
  createWorkspace: (data) => api.post('/admin/marketing/workspaces', data),
  getWorkspace: (id) => api.get(`/admin/marketing/workspaces/${id}`),
  updateWorkspace: (id, data) => api.patch(`/admin/marketing/workspaces/${id}`, data),
  listAssets: (params) => api.get('/admin/marketing/assets', { params }),
  createAsset: (data) => api.post('/admin/marketing/assets', data),
  getAsset: (id) => api.get(`/admin/marketing/assets/${id}`),
  updateAsset: (id, data) => api.patch(`/admin/marketing/assets/${id}`, data),
  listVersions: (id) => api.get(`/admin/marketing/assets/${id}/versions`),
  createVersion: (id, data) => api.post(`/admin/marketing/assets/${id}/versions`, data),
  rollback: (id, versionNumber) => api.post(`/admin/marketing/assets/${id}/rollback`, { version_number: versionNumber }),
  addReference: (id, data) => api.post(`/admin/marketing/assets/${id}/references`, data),
  deleteReference: (id, refId) => api.delete(`/admin/marketing/assets/${id}/references/${refId}`),
  transition: (id, target) => api.post(`/admin/marketing/assets/${id}/transition`, { target }),
  assignReviewers: (id, reviewerIds) => api.post(`/admin/marketing/assets/${id}/reviewers`, { reviewer_ids: reviewerIds }),
  decision: (id, data) => api.post(`/admin/marketing/assets/${id}/decision`, data),
  preview: (id) => api.get(`/admin/marketing/assets/${id}/preview`),
};

// JCEOS Experience Layer (Wave 2) — user-owned preferences + GDPR erasure.
// All three are normal authed calls (a 401 means the session is genuinely invalid, so the
// standard auto-logout is correct) — mirroring journeyAPI.getPreferences. This is user data,
// not an observer read, so it deliberately does NOT use skipAuthRedirect.
export const experienceAPI = {
  getPreferences: () => api.get('/experience/me/preferences'),
  updatePreferences: (data) => api.put('/experience/me/preferences', data),
  deleteMyData: () => api.delete('/experience/me'),
};

export default api;
